<template>
  <div class="border-t border-growth-50 pt-2 mt-2">
    <button @click="expanded = !expanded" class="text-xs text-growth-600 hover:text-growth-tangerine flex items-center gap-1 transition-colors">
      <span>{{ expanded ? '▾' : '▸' }}</span>
      <span>{{ expanded ? 'Fewer filters' : 'More filters' }}</span>
      <span v-if="activeCount > 0" class="bg-growth-tangerine/15 text-growth-tangerine px-1.5 py-0.5 rounded-full text-xs font-medium">{{ activeCount }}</span>
    </button>

    <template v-if="expanded">
      <div v-for="group in groups" :key="group.key" class="mt-3">
        <!-- Group header -->
        <button @click="toggleGroup(group.key)" class="flex items-center gap-1.5 text-xs font-semibold text-growth-700 uppercase tracking-wide mb-1.5 hover:text-growth-tangerine transition-colors w-full">
          <span>{{ openGroups.includes(group.key) ? '▾' : '▸' }}</span>
          <span>{{ group.label }}</span>
          <span v-if="groupActiveCount(group.key) > 0" class="bg-growth-tangerine/15 text-growth-tangerine px-1.5 py-0.5 rounded-full text-[10px] font-medium">{{ groupActiveCount(group.key) }}</span>
        </button>

        <!-- Group dimensions -->
        <div v-if="openGroups.includes(group.key)" class="ml-3 space-y-2">
          <div v-for="dim in dimensionsForGroup(group.key)" :key="dim.key" class="flex items-start gap-2">
            <span class="text-xs text-growth-600 w-20 shrink-0 pt-0.5">{{ dim.label }}</span>

            <!-- Chips display (few options) -->
            <div v-if="dim.display_type === 'chips'" class="flex flex-wrap gap-1">
              <button
                v-for="opt in flatOptions(dim)"
                :key="opt.value"
                @click="toggleFilter(dim.key, opt.value)"
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-all border',
                  isSelected(dim.key, opt.value)
                    ? 'bg-growth-tangerine text-white border-growth-tangerine'
                    : 'bg-white text-growth-600 border-growth-50 hover:border-growth-600'
                ]"
              >{{ opt.label }}</button>
            </div>

            <!-- Select display (many options, like ACO Performance) -->
            <div v-else>
              <select
                :value="filter[dim.key] || ''"
                @change="selectFilter(dim.key, $event.target.value)"
                class="border border-growth-50 rounded px-2 py-1 text-xs bg-white focus:ring-2 focus:ring-growth-tangerine focus:border-growth-tangerine cursor-pointer hover:border-growth-600"
              >
                <option value="">Any</option>
                <template v-if="dim.option_groups">
                  <optgroup v-for="grp in dim.option_groups" :key="grp.label" :label="grp.label">
                    <option v-for="opt in grp.options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  </optgroup>
                </template>
                <template v-else>
                  <option v-for="opt in dim.options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </template>
              </select>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'TagFilterPanel',
  props: {
    dimensions: { type: Array, default: () => [] },
    groups: { type: Array, default: () => [] },
    filter: { type: Object, required: true },
  },
  emits: ['update:filter', 'search'],
  data() {
    return {
      expanded: false,
      openGroups: [],
    }
  },
  computed: {
    activeCount() {
      let count = 0
      for (const dim of this.dimensions) {
        if (this.filter[dim.key]) count++
      }
      return count
    },
  },
  watch: {
    activeCount(val) {
      // Auto-expand if filters are active
      if (val > 0) this.expanded = true
    },
  },
  methods: {
    dimensionsForGroup(groupKey) {
      return this.dimensions.filter(d => d.group === groupKey)
    },
    groupActiveCount(groupKey) {
      return this.dimensionsForGroup(groupKey).filter(d => this.filter[d.key]).length
    },
    flatOptions(dim) {
      if (dim.options) return dim.options
      if (dim.option_groups) {
        const result = []
        for (const g of dim.option_groups) result.push(...g.options)
        return result
      }
      return []
    },
    isSelected(dimKey, value) {
      const current = this.filter[dimKey] || ''
      return current.split(',').includes(value)
    },
    toggleFilter(dimKey, value) {
      const current = this.filter[dimKey] || ''
      const selected = current ? current.split(',') : []
      const idx = selected.indexOf(value)
      if (idx >= 0) {
        selected.splice(idx, 1)
      } else {
        selected.push(value)
      }
      this.$emit('update:filter', { ...this.filter, [dimKey]: selected.join(',') })
      this.$emit('search')
    },
    selectFilter(dimKey, value) {
      this.$emit('update:filter', { ...this.filter, [dimKey]: value })
      this.$emit('search')
    },
    toggleGroup(groupKey) {
      const idx = this.openGroups.indexOf(groupKey)
      if (idx >= 0) {
        this.openGroups.splice(idx, 1)
      } else {
        this.openGroups.push(groupKey)
      }
    },
  },
}
</script>
